import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { useTradeRoutesConditions } from '@pages/Trade/Trade.routes'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useProductReadContext } from '@context/ProductContext'

import { CandlestickIcon } from '@ui/icons/Candlestick.icon'
import { ChartBarIcon } from '@ui/icons/ChartBar.icon'
import { FileSearchIcon } from '@ui/icons/FileSearch.icon'
import { HamburgerIcon } from '@ui/icons/Hamburger.icon'
import { HistoryIcon } from '@ui/icons/History.icon'
import { LayoutIcon } from '@ui/icons/Layout.icon'
import { SuitcaseIcon } from '@ui/icons/Suitcase.icon'
import { UserGroupIcon } from '@ui/icons/UserGroup.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'
import { IconFCType } from '@ui/icons/types'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { isTickmillProductTypeETD } from '@model/TickmillProductType'

export interface MobileTab {
  id: string
  label: string
  icon: IconFCType
  path?: string
  isMenu?: boolean
}

export const useMobileBottomTabs = (): MobileTab[] => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { product } = useProductReadContext()
  const entity = useSessionEntity()
  const { isIBVisible } = useIBRoutesConditions()
  const { canAccessTrade } = useTradeRoutesConditions()
  const navMode = localStorage.getItem('navigationMode')

  const isIBRoom = isIBVisible && !!account?.clientIntroducingBroker?.id
  const hasIBRoom = !!account?.visibilityConfiguration?.hasIBRoom
  const isETD = isTickmillProductTypeETD(product)
  const isTMSC = entity === TickmillCompaniesEnum.TICKMILL_SC
  const isTradingEnabled = isTMSC && canAccessTrade

  return useMemo(() => {
    const menuTab: MobileTab = {
      id: 'menu',
      label: t('Menu'),
      icon: HamburgerIcon,
      isMenu: true,
    }

    if (isIBRoom && hasIBRoom && navMode === 'partner') {
      return [
        {
          id: 'dashboard',
          label: t('Tabs.Dashboard'),
          icon: LayoutIcon,
          path: CommonPaths.IntroducingBrokerDashboard,
        },
        {
          id: 'clients',
          label: t('Tabs.Clients'),
          icon: UserGroupIcon,
          path: '/dashboard/introducing-broker/rebates/clients',
        },
        {
          id: 'reports',
          label: t('Tabs.Reports'),
          icon: FileSearchIcon,
          path: '/dashboard/introducing-broker/reports/clients',
        },
        menuTab,
      ]
    }

    if (isETD) {
      return [
        {
          id: 'balances',
          label: t('Tabs.Balances'),
          icon: WalletIcon,
          path: CommonPaths.TradersRoomBalances,
        },
        {
          id: 'history',
          label: t('Tabs.History'),
          icon: HistoryIcon,
          path: CommonPaths.TradersRoomTransactionHistory,
        },
        menuTab,
      ]
    }

    if (isTradingEnabled) {
      return [
        {
          id: 'accounts',
          label: t('Tabs.Accounts'),
          icon: CandlestickIcon,
          path: '/dashboard/traders-room/trading-accounts/live',
        },
        {
          id: 'markets',
          label: t('Tabs.Markets'),
          icon: ChartBarIcon,
          path: '/dashboard/trade/markets',
        },
        {
          id: 'portfolio',
          label: t('Tabs.Portfolio'),
          icon: SuitcaseIcon,
          path: '/dashboard/trade/portfolio',
        },
        menuTab,
      ]
    }

    // CFD without trading (SC/EU/UK)
    return [
      {
        id: 'accounts',
        label: t('Tabs.Accounts'),
        icon: CandlestickIcon,
        path: '/dashboard/traders-room/trading-accounts/live',
      },
      {
        id: 'wallets',
        label: t('Tabs.Wallets'),
        icon: WalletIcon,
        path: '/dashboard/traders-room/wallets',
      },
      {
        id: 'history',
        label: t('Tabs.History'),
        icon: HistoryIcon,
        path: CommonPaths.TradersRoomTransactionHistory,
      },
      menuTab,
    ]
  }, [t, isIBRoom, hasIBRoom, isETD, isTradingEnabled, navMode])
}
